import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  ArrowDownToLine,
  ArrowUpFromLine,
  Scale,
  Layers,
  Info,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_stock_ledger_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

export default function StockLedgerReport() {
  const { toast } = useToast();

  // Filter states
  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [ingredientId, setIngredientId] = useState("all");
  const [departmentId, setDepartmentId] = useState("all");
  const [source, setSource] = useState("all");
  const [transactionType, setTransactionType] = useState("all");

  // Filter dropdown data
  const [rawItems, setRawItems] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Backend exact match karta hai — ye StockLogs.entry_source ki asli values hain
  const SOURCE_OPTIONS = [
    { value: "OPENING_STOCK", label: "Opening Stock" },
    { value: "MATERIAL_RECEIVING", label: "Purchase (GRN)" },
    { value: "GRN_REVERSED", label: "GRN Reversed" },
    { value: "STORE_ISSUE", label: "Transfer" },
    { value: "ORDER_CONSUMPTION", label: "Sale" },
    { value: "STOCK_ADJUSTMENT", label: "Adjustment" },
    { value: "AUDIT_ADJUSTMENT", label: "Audit" },
  ];

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats (from API summary)
  const [stats, setStats] = useState({
    totalIn: 0,
    totalOut: 0,
    closingBalance: 0,
    totalRecords: 0,
  });

  // Format number
  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch dropdown filter data (ingredients + departments)
  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        setRawItems(response.data.message.raw_items || []);
        setDepartments(response.data.message.departments || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  // Fetch ledger data
  const fetchLedgerData = useCallback(
    async (start_date, end_date, flag = "1") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      if (ingredientId !== "all") fd.append("ingredient_id", ingredientId);
      if (departmentId !== "all") fd.append("department_id", departmentId);
      if (source !== "all") fd.append("source", source);
      if (transactionType !== "all")
        fd.append("transaction_type", transactionType);

      try {
        const response = await server_post_data(get_stock_ledger_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const raw = response.data.data || [];
          const summary = response.data.summary || {};

          // Backend ab qty ingredient ke apne unit me bhejta hai (13 KG,
          // pehle 13000). Balance har ingredient + department ka apna hai.
          const data = raw.map((r) => ({
            ...r,
            primary_id: r.log_id ?? r.primary_id,
            source: r.source_label || r.source,
            amount: r.value ?? r.amount,
            qty_before: r.balance_before ?? r.qty_before,
            qty_current: r.balance_after ?? r.qty_current,
            running_balance: r.balance_after ?? r.running_balance,
          }));

          // Alag unit ki quantity (gram + ml) jodna galat tha —
          // summary ab ₹ value me hai
          const closingValue = (summary.ingredient_wise || []).reduce(
            (sum, i) => sum + (Number(i.closing_value) || 0),
            0,
          );

          setLedgerData(data);
          setStats({
            totalIn: summary.total_in_value ?? summary.total_in ?? 0,
            totalOut: summary.total_out_value ?? summary.total_out ?? 0,
            closingBalance: closingValue || summary.closing_balance || 0,
            totalRecords: response.data.total_records || data.length,
          });
        }
      } catch (error) {
        console.error("Error fetching stock ledger:", error);
        toast({
          title: "Error",
          description: "Failed to load stock ledger data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [
      filterOption,
      startCal,
      endCal,
      ingredientId,
      departmentId,
      source,
      transactionType,
      toast,
    ],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchLedgerData(startCal, endCal, "2");
  }, [startCal, endCal, fetchLedgerData]);

  // Excel export
  function downloadExcel(orderData, filename = "Stock_Ledger_Report.xlsx") {
    if (!orderData.length) {
      handleError("No data available for export!");
      return;
    }

    const formattedData = orderData.map((item, index) => ({
      "Sr. No.": index + 1,
      Date: item.date || "-",
      Ingredient: item.ingredient_name || "-",
      Department: item.department_name || "-",
      Source: item.source || "-",
      Type: item.transaction_type,
      Quantity: formatNumber(item.quantity),
      Unit: item.unit || "-",
      "Rate (₹)": formatNumber(item.rate),
      "Amount (₹)": formatNumber(item.amount),
      "Amount w/o GST (₹)": formatNumber(item.price_total_without_gst),
      "Qty Before": formatNumber(item.qty_before),
      "Qty Current": formatNumber(item.qty_current),
      "Running Balance": formatNumber(item.running_balance),
      "Reference ID": item.reference_id || "-",
    }));

    // Summary row
    formattedData.push({
      "Sr. No.": "",
      Date: "",
      Ingredient: "",
      Department: "",
      Source: "TOTAL",
      Type: `IN: ₹${formatNumber(stats.totalIn)} / OUT: ₹${formatNumber(
        stats.totalOut,
      )}`,
      Quantity: "",
      Unit: "",
      "Rate (₹)": "",
      "Amount (₹)": "",
      "Amount w/o GST (₹)": "",
      "Qty Before": "",
      "Qty Current": "",
      "Running Balance": `₹${formatNumber(stats.closingBalance)}`,
      "Reference ID": "",
    });

    const sheet = XLSX.utils.json_to_sheet(formattedData);
    sheet["!cols"] = [
      { wch: 8 },
      { wch: 20 },
      { wch: 22 },
      { wch: 18 },
      { wch: 14 },
      { wch: 20 },
      { wch: 10 },
      { wch: 8 },
      { wch: 12 },
      { wch: 12 },
      { wch: 16 },
      { wch: 12 },
      { wch: 12 },
      { wch: 16 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Stock Ledger");
    XLSX.writeFile(workbook, filename);
  }

  // Pagination
  const totalPages = Math.ceil(ledgerData.length / entriesPerPage);
  const currentData = ledgerData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Format date for table display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString.replace(" ", "T")).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Initial load
  useEffect(() => {
    fetchFilterData();
    fetchLedgerData(startCal, endCal, "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout>
      <Header
        title="Stock Ledger Report"
        subtitle="Complete stock movement history with running balance"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <ArrowDownToLine className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{formatNumber(stats.totalIn)}
                </p>
                <p className="text-sm text-muted-foreground">Stock IN Value</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <ArrowUpFromLine className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{formatNumber(stats.totalOut)}
                </p>
                <p className="text-sm text-muted-foreground">Stock OUT Value</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Scale className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-2xl font-bold text-foreground">
                    ₹{formatNumber(stats.closingBalance)}
                  </p>
                  <Info
                    className="w-3.5 h-3.5 text-muted-foreground shrink-0"
                    title="Value of the closing stock for the ingredients and departments in this report, at average landed cost."
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Closing Stock Value
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Layers className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalRecords}
                </p>
                <p className="text-sm text-muted-foreground">Total Records</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Ingredient */}
              <div className="space-y-2">
                <Label>Ingredient</Label>
                <Select value={ingredientId} onValueChange={setIngredientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Ingredients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ingredients</SelectItem>
                    {rawItems.map((item) => (
                      <SelectItem
                        key={item.primary_id}
                        value={String(item.primary_id)}
                      >
                        {item.ingredient_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Department */}
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.primary_id}
                        value={String(dept.primary_id)}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Source */}
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={source} onValueChange={setSource}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {SOURCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Transaction Type */}
              <div className="space-y-2">
                <Label>Transaction Type</Label>
                <Select
                  value={transactionType}
                  onValueChange={setTransactionType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="ADD">IN (Add)</SelectItem>
                    <SelectItem value="REMOVE">OUT (Remove)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(ledgerData)}
                  className="gap-2"
                  disabled={ledgerData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">
                    Ingredient
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Source
                  </TableHead>
                  <TableHead className="text-muted-foreground">Type</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Quantity
                  </TableHead>
                  <TableHead className="text-muted-foreground">Unit</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Rate (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Amount (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Qty Before
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Qty Current
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Running Balance
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Reference
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={14}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={14}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <TableRow
                      key={row.primary_id || index}
                      className="border-border"
                    >
                      <TableCell>
                        {(currentPage - 1) * entriesPerPage + index + 1}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(row.date)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.ingredient_name}
                      </TableCell>
                      <TableCell>{row.department_name}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium">
                          {row.source || "N/A"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
                            row.transaction_type === "IN"
                              ? "bg-success/10 text-success"
                              : "bg-destructive/10 text-destructive",
                          )}
                        >
                          {row.transaction_type}
                        </span>
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-medium",
                          row.transaction_type === "IN"
                            ? "text-success"
                            : "text-destructive",
                        )}
                      >
                        {row.transaction_type === "IN" ? "+" : "-"}
                        {formatNumber(row.quantity)}
                      </TableCell>
                      <TableCell>{row.unit || "-"}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.rate)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.qty_before)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.qty_current)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatNumber(row.running_balance)}
                      </TableCell>
                      <TableCell>{row.reference_id || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, ledgerData.length)} of{" "}
                {ledgerData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
